import { getApiBaseUrl } from '@beatq/sdk-client';
import { getAccessToken } from '@beatq/shared';
import { mapErrorCode, readListFromEnvelope } from '@beatq/shared';
import { isWithinMerchantInboxWindow } from './merchantInbox.js';

type ApiErr = { error?: { code: string; message: string } };

function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

export type MerchantInboxMessage = {
  id: string;
  category?: string;
  title?: string;
  body?: string;
  deepLink?: string | null;
  deep_link?: string | null;
  isRead?: boolean;
  is_read?: boolean;
  read?: boolean;
  requiresAction?: boolean;
  createdAt?: string;
  created_at?: string;
  semanticSubject?: string | null;
  semantic_subject?: string | null;
  subjectKind?: string | null;
  subjectId?: string | null;
  branchId?: string | null;
  branch_id?: string | null;
  branchName?: string | null;
};

export type MerchantInboxMeta = {
  unreadCount?: number;
  actionRequiredCount?: number;
  nextCursor?: string | null;
};

function merchantApiUrl(path: string): string {
  const base = getApiBaseUrl().replace(/\/$/, '');
  return path.startsWith('http') ? path : `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

async function merchantFetch(path: string, init?: RequestInit) {
  const token = getAccessToken();
  const res = await fetch(merchantApiUrl(path), {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(sdkErrorMessage(json));
  return json;
}

export async function listMerchantNotifications(query?: {
  unreadOnly?: boolean;
  category?: string;
  cursor?: string;
  pageSize?: number;
}) {
  const params = new URLSearchParams();
  if (query?.unreadOnly) params.set('unreadOnly', 'true');
  if (query?.category) params.set('category', query.category);
  if (query?.cursor) params.set('cursor', query.cursor);
  if (query?.pageSize) params.set('pageSize', String(query.pageSize));
  const qs = params.toString();
  return merchantFetch(`/merchant/v1/notifications${qs ? `?${qs}` : ''}`);
}

/** Pages GET /merchant/v1/notifications until the 24-month window is exhausted. */
export async function listMerchantInboxWindow(query?: {
  unreadOnly?: boolean;
  category?: string;
}): Promise<{ messages: MerchantInboxMessage[]; meta: MerchantInboxMeta }> {
  const messages: MerchantInboxMessage[] = [];
  let meta: MerchantInboxMeta = {};
  let cursor: string | undefined;
  const now = new Date();

  for (let page = 0; page < 20; page += 1) {
    const envelope = await listMerchantNotifications({
      ...query,
      cursor,
      pageSize: 50,
    });
    const batch = messagesFromEnvelope(envelope);
    meta = inboxMetaFromEnvelope(envelope);
    if (batch.length === 0) break;

    for (const item of batch) {
      if (isWithinMerchantInboxWindow(item.createdAt, now)) messages.push(item);
    }

    const oldest = batch[batch.length - 1];
    if (!oldest || !isWithinMerchantInboxWindow(oldest.createdAt, now)) break;
    const next = meta.nextCursor;
    if (!next) break;
    cursor = next;
  }

  return { messages, meta };
}

export async function markMerchantNotificationRead(id: string) {
  return merchantFetch(`/merchant/v1/notifications/${encodeURIComponent(id)}/read`, {
    method: 'POST',
    body: '{}',
  });
}

export async function markAllMerchantNotificationsRead() {
  return merchantFetch('/merchant/v1/notifications/read-all', {
    method: 'POST',
    body: '{}',
  });
}

export function messagesFromEnvelope(envelope: unknown): MerchantInboxMessage[] {
  return readListFromEnvelope<MerchantInboxMessage>(envelope, ['data', 'messages', 'items']).map(
    normalizeMerchantInboxMessage,
  );
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function normalizeMerchantInboxMessage(raw: MerchantInboxMessage): MerchantInboxMessage {
  const semanticSubject = asString(raw.semanticSubject) ?? asString(raw.semantic_subject);
  const branchId = asString(raw.branchId) ?? asString(raw.branch_id);
  const deepLink = asString(raw.deepLink) ?? asString(raw.deep_link);
  const createdAt = asString(raw.createdAt) ?? asString(raw.created_at);
  return {
    ...raw,
    semanticSubject,
    semantic_subject: semanticSubject,
    branchId,
    branch_id: branchId,
    deepLink,
    deep_link: deepLink,
    createdAt: createdAt ?? raw.createdAt,
    isRead: raw.isRead === true || raw.is_read === true || raw.read === true,
  };
}

export function inboxMetaFromEnvelope(envelope: unknown): MerchantInboxMeta {
  if (envelope == null || typeof envelope !== 'object') return {};
  const record = envelope as { meta?: MerchantInboxMeta; data?: { meta?: MerchantInboxMeta } };
  return record.data?.meta ?? record.meta ?? {};
}
